import { cn } from "@lib/cn"
import type { ComponentType } from "react"

export interface SegmentedOption<T extends string | number> {
  value: T
  label: string
  /** A count shown after the label ("Papelera 3"). Omitted when undefined. */
  count?: number
  /** Any icon component that takes a className — lucide's and our own. */
  icon?: ComponentType<{ className?: string }>
  disabled?: boolean
  /** Why this option cannot be chosen. Rendered as a title AND, by the
   *  caller, as visible text — a tooltip alone is unreachable on a tablet. */
  tooltip?: string
}

/**
 * A row of mutually exclusive choices (PRD-60 chunk 0), the same control as
 * the professor SPA's. The console had two hand-rolled radiogroups (the theme
 * picker, the real/demo data toggle) that neither moved with the arrow keys
 * nor shared a shape.
 *
 * A radiogroup, not a row of buttons: arrow keys move between options and a
 * screen reader announces "2 de 3" instead of three unrelated buttons.
 */
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: T
  options: SegmentedOption<T>[]
  onChange: (v: T) => void
  ariaLabel?: string
  className?: string
}) {
  const enabled = options.filter((o) => !o.disabled)

  const move = (delta: number) => {
    if (enabled.length === 0) return
    const at = enabled.findIndex((o) => o.value === value)
    const next = enabled[(((at < 0 ? 0 : at) + delta + enabled.length) % enabled.length)!]
    if (next) onChange(next.value)
  }

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn("flex flex-wrap gap-2", className)}>
      {options.map((opt) => {
        const Icon = opt.icon
        const active = value === opt.value
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={active}
            // Only the selected option is a tab stop; arrows move within.
            tabIndex={active ? 0 : -1}
            disabled={opt.disabled}
            title={opt.tooltip}
            onClick={() => !opt.disabled && onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault()
                move(1)
              }
              if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault()
                move(-1)
              }
            }}
            className={cn(
              "rounded-control inline-flex min-h-11 items-center gap-1.5 border px-3 text-sm font-medium transition-colors duration-150 md:min-h-9",
              active
                ? "border-accent bg-accent-soft text-accent-ink"
                : "border-border bg-surface text-ink-muted hover:border-border-strong hover:bg-inset",
              opt.disabled && "cursor-not-allowed opacity-50",
            )}
          >
            {Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
            {opt.label}
            {opt.count !== undefined && (
              <span
                className={cn(
                  "text-xs tabular-nums",
                  active ? "text-accent-ink" : "text-ink-subtle",
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
